import React, { useEffect, useState } from "react";
import { User, Target, Eye, ShieldCheck } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { VALUES, ABOUT_IMAGE, TRUST } from "../data/content";

const initials = (name) =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

const AboutPage = () => {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Header banner */}
      <section className="bg-[#0F2942] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest font-semibold text-amber-400">About Us</span>
          <h1 className="font-display mt-3 text-4xl sm:text-5xl font-extrabold text-white">Who We Are</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            A registered public charitable trust headquartered in Deokali, Obra, Aurangabad, Bihar —
            devoted to equality, education and service.
          </p>
        </div>
      </section>

      {/* History */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <img src={ABOUT_IMAGE} alt="Community" className="rounded-3xl shadow-xl w-full h-[380px] object-cover" />
          <div>
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Our History</span>
            <h2 className="font-display mt-3 text-3xl font-bold text-slate-900">Rooted in compassion, built for change</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Lord Buddha Awareness And Development Foundation is a registered public charitable trust
              with its head office in Deokali (Obra, Aurangabad, Bihar). Inspired by Lord Buddha's values
              of compassion, harmony and peace, the foundation actively works on the ground for social
              justice, women empowerment and youth development in rural and semi-urban regions.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Our purpose is to bring free education, better health and dignified living opportunities to
              the deprived, backward and poor sections of society.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-800 text-sm font-medium px-4 py-2 rounded-full">
                <ShieldCheck className="h-4 w-4" /> Registered {TRUST.regDate}
              </span>
              <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-800 text-sm font-medium px-4 py-2 rounded-full">
                President: {TRUST.president}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-7">
          <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
            <div className="h-12 w-12 rounded-xl bg-blue-50 ring-4 ring-blue-100 flex items-center justify-center mb-5">
              <Eye className="h-6 w-6 text-blue-700" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900">Our Vision</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">
              To build an inclusive society where no individual faces discrimination on the basis of caste,
              gender or economic status — where every child receives quality education and every family
              access to basic facilities.
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm">
            <div className="h-12 w-12 rounded-xl bg-emerald-50 ring-4 ring-emerald-100 flex items-center justify-center mb-5">
              <Target className="h-6 w-6 text-emerald-700" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900">Our Mission</h3>
            <ul className="mt-3 space-y-2 text-slate-600 text-sm leading-relaxed list-disc pl-5">
              <li>Free coaching, classes and study material for poor and needy children.</li>
              <li>Free medical camps and immediate relief during emergencies and natural disasters.</li>
              <li>Awareness against child marriage, dowry and child labour; promoting human rights.</li>
              <li>Vocational training and guidance for women and persons with disabilities.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Our Values</span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-slate-900">Guided by Buddhist Ideals</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm card-lift text-center">
              <p className="font-serif-accent text-2xl font-semibold text-amber-600">{v.title}</p>
              <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold mt-1">{v.subtitle}</p>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-widest font-semibold text-emerald-700">Leadership</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-slate-900">Our Team Members</h2>
            <p className="mt-4 text-slate-600">The dedicated trustees driving our mission forward.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="team-grid">
            {team.map((m) => (
              <div key={m.id} className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm card-lift">
                <div className="h-56 bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center overflow-hidden">
                  {m.photo_path ? (
                    <img src={fileUrl(m.photo_path)} alt={m.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="h-24 w-24 rounded-full bg-[#0F2942] text-white flex items-center justify-center font-display text-3xl font-bold">
                      {initials(m.name)}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-bold text-slate-900">{m.name}</h3>
                  <p className="text-emerald-700 text-sm font-semibold">{m.role}</p>
                  {m.bio && <p className="mt-2 text-sm text-slate-500 leading-relaxed">{m.bio}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
